/**
 * VISTOERA_AI_COST_PRICING_PROVIDER_COST_POINTS_REVENUE_MARGIN_CONTROL_
 * PLANE_P0_V1 — Platform Admin "AI 成本与定价" page.
 *
 * Single joined list endpoint (`GET /platform/ai-pricing`) — no per-row
 * N+1 fetches (task spec §28). Summary cards intentionally avoid fake
 * "per 1k calls" aggregates when no reliable usage volume exists
 * (task spec §7).
 */

import {
  AccountBookOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import {
  Alert,
  Button,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  platformGetAiPricingReferenceConfig,
  platformListAiPricing,
  platformUpdateAiPricingEntry,
  platformUpdateAiPricingReferenceConfig,
} from '../api'
import type {
  AiPricingEntryRow,
  AiPricingFunctionNodeOption,
  AiPricingReferenceConfig,
  AiPricingSummary,
} from '../types'
import AIPricingEntryDrawer from './AIPricingEntryDrawer'
import {
  financialStatusLabelZh,
  formatMarginPercent,
  formatMoneyAmount,
  formatPointConsumption,
  marginTone,
  type AiPricingFinancialStatus,
} from './aiPricingCalculations'
import { SectionCard, SummaryCard } from './adminShared'
import { cardIconBg, platformColors } from './theme'

export default function AIPricingPage() {
  const [rows, setRows] = useState<AiPricingEntryRow[]>([])
  const [summary, setSummary] = useState<AiPricingSummary | null>(null)
  const [nodeOptions, setNodeOptions] = useState<AiPricingFunctionNodeOption[]>([])
  const [referenceConfig, setReferenceConfig] = useState<AiPricingReferenceConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [functionNode, setFunctionNode] = useState<string | undefined>()
  const [provider, setProvider] = useState<string | undefined>()
  const [model, setModel] = useState<string | undefined>()
  const [executionOption, setExecutionOption] = useState<string | undefined>()
  const [status, setStatus] = useState<string | undefined>()
  const [search, setSearch] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [lossMakingOnly, setLossMakingOnly] = useState(false)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('edit')
  const [editing, setEditing] = useState<AiPricingEntryRow | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const [refValueDraft, setRefValueDraft] = useState<number | null>(null)
  const [savingRef, setSavingRef] = useState(false)
  const [togglingId, setTogglingId] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    Promise.all([
      platformListAiPricing({
        function_node: functionNode,
        provider,
        model,
        status,
        search: appliedSearch.trim() || undefined,
        loss_making_only: lossMakingOnly || undefined,
      }),
      platformGetAiPricingReferenceConfig(),
    ])
      .then(([listRes, refRes]) => {
        setRows(listRes.data.items)
        setSummary(listRes.data.summary)
        setNodeOptions(listRes.data.function_node_options)
        setReferenceConfig(refRes.data)
        setRefValueDraft(
          refRes.data.reference_point_value != null ? Number(refRes.data.reference_point_value) : null,
        )
      })
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false))
  }, [functionNode, provider, model, status, appliedSearch, lossMakingOnly])

  useEffect(() => {
    load()
  }, [load])

  const providerOptions = useMemo(
    () => uniqueSorted(rows.map((r) => r.provider_key)),
    [rows],
  )
  const modelOptions = useMemo(() => uniqueSorted(rows.map((r) => r.model_key)), [rows])
  const executionOptions = useMemo(
    () => uniqueSorted(rows.map((r) => r.execution_option_label)),
    [rows],
  )

  const displayRows = useMemo(() => {
    if (!executionOption) return rows
    return rows.filter((r) => r.execution_option_label === executionOption)
  }, [rows, executionOption])

  const resetFilters = () => {
    setFunctionNode(undefined)
    setProvider(undefined)
    setModel(undefined)
    setExecutionOption(undefined)
    setStatus(undefined)
    setSearch('')
    setAppliedSearch('')
    setLossMakingOnly(false)
  }

  const openCreate = () => {
    setDrawerMode('create')
    setEditing(null)
    setDrawerOpen(true)
  }

  const openEdit = (row: AiPricingEntryRow) => {
    setDrawerMode('edit')
    setEditing(row)
    setDrawerOpen(true)
  }

  const saveReference = async () => {
    setSavingRef(true)
    try {
      const res = await platformUpdateAiPricingReferenceConfig({
        reference_point_value: refValueDraft,
        reference_currency: referenceConfig?.reference_currency ?? 'CNY',
      })
      setReferenceConfig(res.data)
      message.success('积分参考价值已更新（仅影响未来估算）')
      load()
    } catch (err) {
      message.error((err as Error).message)
    } finally {
      setSavingRef(false)
    }
  }

  const toggleEnabled = async (row: AiPricingEntryRow, enabled: boolean) => {
    setTogglingId(row.id)
    try {
      const res = await platformUpdateAiPricingEntry(row.id, {
        enabled,
        pricing_status: enabled && row.pricing_status === 'PRICING_REQUIRED' ? 'ACTIVE' : row.pricing_status,
      })
      setRows((prev) => prev.map((r) => (r.id === row.id ? res.data : r)))
    } catch (err) {
      message.error((err as Error).message)
    } finally {
      setTogglingId(null)
    }
  }

  const toneColor = (tone: ReturnType<typeof marginTone>) => {
    switch (tone) {
      case 'green':
        return platformColors.success
      case 'orange':
        return platformColors.warning
      case 'yellow':
        return '#CA8A04'
      case 'red':
        return platformColors.danger
      default:
        return platformColors.secondaryText
    }
  }

  const columns: ColumnsType<AiPricingEntryRow> = [
    {
      title: '功能节点',
      key: 'function_node',
      width: 140,
      render: (_, row) => (
        <Space size={6}>
          <span style={{ fontSize: 16 }}>{nodeIcon(row.function_node_key)}</span>
          <span>{row.function_node_label_zh}</span>
        </Space>
      ),
    },
    {
      title: '执行方案',
      dataIndex: 'execution_option_label',
      width: 160,
      render: (v: string, row) => (
        <span>
          {v}
          {!row.execution_option_linked && (
            <Tag style={{ marginLeft: 6 }} color="default">
              未关联
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: 'Provider / Model',
      key: 'provider_model',
      width: 180,
      render: (_, row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{row.provider_label}</div>
          <div style={{ color: platformColors.secondaryText, fontSize: 12 }}>{row.model_label}</div>
          {row.provider_credential_label && (
            <div style={{ color: platformColors.mutedText, fontSize: 11 }}>{row.provider_credential_label}</div>
          )}
        </div>
      ),
    },
    {
      title: '计费单位',
      dataIndex: 'billing_unit_label_zh',
      width: 90,
    },
    {
      title: '供应商成本',
      key: 'cost',
      width: 110,
      render: (_, row) => formatMoneyAmount(row.supplier_cost_per_unit, row.cost_currency),
    },
    {
      title: '积分消耗',
      key: 'points',
      width: 100,
      render: (_, row) => formatPointConsumption(row.points_per_unit),
    },
    {
      title: '积分收入折算',
      key: 'revenue',
      width: 120,
      render: (_, row) => {
        if (row.financial_status === 'NO_REFERENCE_POINT_VALUE') {
          return <Typography.Text type="secondary">未设置参考价值</Typography.Text>
        }
        return formatMoneyAmount(row.revenue_per_unit, row.reference_currency)
      },
    },
    {
      title: '毛利',
      key: 'profit',
      width: 100,
      render: (_, row) => {
        if (row.financial_status === 'CURRENCY_MISMATCH') {
          return <Typography.Text type="warning">待汇率</Typography.Text>
        }
        if (row.gross_profit_per_unit == null) {
          return financialStatusLabelZh(row.financial_status as AiPricingFinancialStatus)
        }
        const n = Number(row.gross_profit_per_unit)
        return (
          <span style={{ color: n < 0 ? platformColors.danger : undefined, fontWeight: n < 0 ? 600 : undefined }}>
            {formatMoneyAmount(row.gross_profit_per_unit, row.reference_currency)}
          </span>
        )
      },
    },
    {
      title: '毛利率',
      key: 'margin',
      width: 90,
      render: (_, row) => {
        if (row.financial_status === 'CURRENCY_MISMATCH') {
          return <Typography.Text type="warning">待汇率</Typography.Text>
        }
        const m = row.gross_margin != null ? Number(row.gross_margin) : null
        return (
          <span style={{ color: toneColor(marginTone(m)), fontWeight: 600 }}>
            {formatMarginPercent(m)}
          </span>
        )
      },
    },
    {
      title: '状态',
      key: 'enabled',
      width: 90,
      render: (_, row) => (
        <Switch
          size="small"
          checked={row.enabled}
          loading={togglingId === row.id}
          onChange={(v) => void toggleEnabled(row, v)}
        />
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 70,
      fixed: 'right',
      render: (_, row) => (
        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
      ),
    },
  ]

  const avgMargin =
    summary?.average_gross_margin != null ? Number(summary.average_gross_margin) : null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <Typography.Title level={3} style={{ margin: 0 }}>
            AI 成本与定价
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ margin: '6px 0 0' }}>
            管理各 AI 执行方案的供应商成本、积分消耗与毛利。实际财务以运行时冻结的计费快照为准。
          </Typography.Paragraph>
        </div>
        <Button icon={<InfoCircleOutlined />} onClick={() => setHelpOpen(true)}>
          计价说明
        </Button>
      </div>

      <Alert
        type="info"
        showIcon
        message="修改仅影响未来产生的计费，历史计费快照不会追溯变化。"
      />

      {summary && summary.loss_making_count > 0 && (
        <Alert
          type="error"
          showIcon
          icon={<WarningOutlined />}
          message={`⚠ ${summary.loss_making_count} 个执行方案当前定价低于供应商成本`}
          action={
            <Button size="small" danger onClick={() => setLossMakingOnly(true)}>
              仅看亏损
            </Button>
          }
        />
      )}

      {error && <Alert type="error" showIcon message={error} />}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        <SummaryCard
          title="已配置执行方案"
          loading={loading}
          value={summary?.configured_execution_options ?? '—'}
          hint={summary ? `覆盖 ${summary.distinct_function_nodes} 个功能节点` : undefined}
          iconBg={cardIconBg.task}
          icon={<AccountBookOutlined style={{ color: platformColors.info }} />}
        />
        <SummaryCard
          title="平均毛利率"
          loading={loading}
          value={avgMargin != null ? formatMarginPercent(avgMargin) : '—'}
          hint={summary?.average_gross_margin_basis ?? '基于当前有效定价规则估算'}
          iconBg={cardIconBg.worker}
          icon={<AccountBookOutlined style={{ color: platformColors.success }} />}
        />
        <SummaryCard
          title="已配置积分消耗"
          loading={loading}
          value={
            summary
              ? `${summary.points_price_configured_count} / ${summary.total_rows}`
              : '—'
          }
          hint={
            summary?.average_revenue_per_unit != null
              ? `平均积分收入 ${formatMoneyAmount(summary.average_revenue_per_unit, referenceConfig?.reference_currency)}`
              : '无可靠 usage volume，不伪造每千次收入'
          }
          iconBg={cardIconBg.domain}
          icon={<AccountBookOutlined style={{ color: platformColors.primary }} />}
        />
        <SummaryCard
          title="已配置供应商成本"
          loading={loading}
          value={
            summary
              ? `${summary.supplier_cost_configured_count} / ${summary.total_rows}`
              : '—'
          }
          hint="无可靠 usage volume，不伪造每千次成本"
          iconBg={cardIconBg.storage}
          icon={<AccountBookOutlined style={{ color: platformColors.warning }} />}
        />
      </div>

      <SectionCard title="积分参考价值（Admin 估算专用）">
        <Space wrap>
          <Typography.Text type="secondary">1 point =</Typography.Text>
          <InputNumber
            min={0}
            step={0.001}
            value={refValueDraft}
            onChange={(v) => setRefValueDraft(typeof v === 'number' ? v : null)}
            addonAfter={referenceConfig?.reference_currency ?? 'CNY'}
          />
          <Button type="primary" loading={savingRef} onClick={() => void saveReference()}>
            保存参考价值
          </Button>
          <Typography.Text type="secondary">
            仅用于后台毛利估算，不会追溯改写历史 ledger / snapshot。
          </Typography.Text>
        </Space>
      </SectionCard>

      <SectionCard title="定价列表" allowOverflow>
        <Space wrap style={{ marginBottom: 12 }}>
          <Select
            allowClear
            placeholder="功能节点"
            style={{ minWidth: 140 }}
            value={functionNode}
            onChange={setFunctionNode}
            options={nodeOptions.map((n) => ({ value: n.node_key, label: n.label_zh }))}
          />
          <Select
            allowClear
            placeholder="Provider"
            style={{ minWidth: 140 }}
            value={provider}
            onChange={setProvider}
            options={providerOptions.map((p) => ({ value: p, label: p }))}
          />
          <Select
            allowClear
            placeholder="模型"
            style={{ minWidth: 140 }}
            value={model}
            onChange={setModel}
            options={modelOptions.map((m) => ({ value: m, label: m }))}
          />
          <Select
            allowClear
            placeholder="执行方案"
            style={{ minWidth: 140 }}
            value={executionOption}
            onChange={setExecutionOption}
            options={executionOptions.map((e) => ({ value: e, label: e }))}
          />
          <Select
            allowClear
            placeholder="状态"
            style={{ minWidth: 120 }}
            value={status}
            onChange={setStatus}
            options={[
              { value: 'ACTIVE', label: 'ACTIVE' },
              { value: 'PRICING_REQUIRED', label: 'PRICING_REQUIRED' },
              { value: 'DRAFT', label: 'DRAFT' },
              { value: 'DEPRECATED', label: 'DEPRECATED' },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="搜索模型 / 方案 / Provider"
            style={{ width: 220 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={(v) => setAppliedSearch(v)}
            onClear={() => {
              setSearch('')
              setAppliedSearch('')
            }}
          />
          <Button onClick={resetFilters}>重置</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新增定价
          </Button>
        </Space>

        <Table<AiPricingEntryRow>
          className="pf-data-table"
          rowKey="id"
          loading={loading}
          dataSource={displayRows}
          columns={columns}
          size="small"
          scroll={{ x: 1400 }}
          sticky
          pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
          rowClassName={(row) => (row.is_loss_making ? 'ai-pricing-loss-row' : '')}
        />
      </SectionCard>

      <AIPricingEntryDrawer
        open={drawerOpen}
        mode={drawerMode}
        entry={editing}
        referenceConfig={referenceConfig}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => {
          setDrawerOpen(false)
          load()
        }}
      />

      <Modal
        title="计价说明"
        open={helpOpen}
        onCancel={() => setHelpOpen(false)}
        footer={<Button onClick={() => setHelpOpen(false)}>知道了</Button>}
      >
        <Typography.Paragraph>
          <strong>供应商成本</strong>：调用一次该执行方案，Vistoera 实际向 Provider / Worker 支付的成本。
        </Typography.Paragraph>
        <Typography.Paragraph>
          <strong>积分消耗</strong>：租户调用一次该执行方案被扣除的积分数量（runtime 通过 AI Rate Catalog 计费）。
        </Typography.Paragraph>
        <Typography.Paragraph>
          <strong>积分收入折算</strong>：积分 × 参考积分价值（Admin 估算用），不等于真实历史收款。
        </Typography.Paragraph>
        <Typography.Paragraph>
          <strong>毛利 / 毛利率</strong>：收入 − 成本；毛利 ÷ 收入。不同币种且无汇率时显示「待汇率」。
        </Typography.Paragraph>
      </Modal>
    </div>
  )
}

function uniqueSorted(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.filter((v): v is string => Boolean(v)))).sort()
}

function nodeIcon(nodeKey: string): string {
  switch (nodeKey) {
    case 'generate_image':
    case 'image.generation':
      return '🖼'
    case 'generate_video':
    case 'video.generation':
      return '🎬'
    case 'generate_tryon':
    case 'tryon.clothing':
      return '👗'
    case 'ai_chat':
      return '💬'
    case 'flat_image_process':
      return '📐'
    case 'object_image_process':
      return '📦'
    case 'virtual_ip_image':
    case 'virtual_ip_voice':
    case 'virtual_ip_positioning':
      return '🧑'
    case 'video_planning':
      return '📝'
    default:
      return '⚙️'
  }
}
